import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SectionTitle from "@/components/feature/SectionTitle";
import NewsListItem from "@/components/feature/NewsListItem";
import StateBlock from "@/components/feature/StateBlock";
import { ListSkeleton } from "@/components/feature/NewsSkeleton";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useMockLoading } from "@/hooks/useMockLoading";
import { getAllNews, getFeaturedNews, getHotNews } from "@/lib/news";
import FeaturedCarousel from "./components/FeaturedCarousel";
import CategoryQuickNav from "./components/CategoryQuickNav";
import HotSidebar from "./components/HotSidebar";
import BrandIntro from "./components/BrandIntro";

const PAGE_SIZE = 5;

export default function Home() {
  useDocumentMeta({
    title: "91爆料 - 最新热点新闻与深度爆料平台",
    description:
      "91爆料为您提供最新最热的社会热点新闻和独家爆料报道，涵盖娱乐、时政、科技等多个领域，实时更新直击内幕。",
    keywords: "91爆料,热点新闻,社会爆料,最新新闻,独家报道,新闻平台",
  });

  const { loading, reload } = useMockLoading(600);
  const featured = getFeaturedNews();
  const hotNews = getHotNews(7);
  const [page, setPage] = useState(1);

  const sortedNews = useMemo(
    () => [...getAllNews()].sort((a, b) => (a.publishDate < b.publishDate ? 1 : -1)),
    [],
  );

  const totalPages = Math.ceil(sortedNews.length / PAGE_SIZE);
  const pageNews = sortedNews.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const changePage = (p: number) => {
    setPage(p);
    const el = document.getElementById("latest-news");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6">
        {/* 顶部：焦点轮播 + 分类快捷入口 */}
        <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <FeaturedCarousel items={featured} />
          </div>
          <div className="lg:col-span-1">
            <CategoryQuickNav />
          </div>
        </section>

        {/* 主体：最新新闻 + 热门侧栏 */}
        <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div id="latest-news" className="lg:col-span-2">
            <SectionTitle title="最新新闻" subtitle="实时更新 · 每日精选" icon="ri-flashlight-line" />
            <div className="rounded-lg border border-background-200 bg-background-50 px-5">
              {loading ? (
                <ListSkeleton count={5} />
              ) : pageNews.length === 0 ? (
                <div className="py-8">
                  <StateBlock
                    icon="ri-error-warning-line"
                    title="暂时无法获取新闻"
                    description="内容加载失败，可能是网络异常，请稍后重试。"
                    actionLabel="重新加载"
                    onAction={reload}
                  />
                </div>
              ) : (
                <div>
                  {pageNews.map((news) => (
                    <NewsListItem key={news.id} news={news} />
                  ))}
                </div>
              )}
            </div>

            {!loading && (
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => changePage(page - 1)}
                  className="flex h-9 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-arrow-left-s-line"></i>
                  上一页
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => changePage(i + 1)}
                    className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-sm font-medium transition-colors ${
                      page === i + 1
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={page === totalPages}
                  onClick={() => changePage(page + 1)}
                  className="flex h-9 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  下一页
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>

          <div className="lg:col-span-1 space-y-6">
            <HotSidebar items={hotNews} />
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="mb-3 flex items-center gap-2">
                <span className="flex h-6 w-1.5 rounded-full bg-accent-500"></span>
                <h2 className="font-heading text-lg font-bold text-foreground-900">编辑精选专题</h2>
              </div>
              <p className="text-sm leading-relaxed text-foreground-500">
                从资本博弈到民生现场，91爆料编辑团队为你梳理值得深读的独家专题，帮你快速看懂热点背后的逻辑。
              </p>
              <Link
                to="/category/society"
                className="mt-4 flex cursor-pointer items-center justify-between rounded-md bg-secondary-100 px-4 py-3 text-sm font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
              >
                浏览深度社会专题
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

      </div>
      <BrandIntro />
    </>
  );
}